import React, { useState, useEffect, useRef } from 'react';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { Box, Button, Typography } from '@mui/material';
import { FormRendererAPIType, RawFormComponentType } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import IconMi from 'components/common/icon/IconMi';
import { useQuery } from '@tanstack/react-query';
import { customRequestAPI } from 'api/api';
import MITable from 'components/common/grid/MITable';
import { GridData, TransformedComponentConfig } from 'components/common/grid';
import { Popup } from 'components/common/popup/Popup';
import { useForm } from 'react-hook-form';
import styles from 'components/common/image-list-handler/ImageListHandler.styles';
import FormStaticLabel from 'components/common/form/layout/static/FormStaticLabel';
import FormStaticInfo from 'components/common/form/layout/static/FormStaticInfo';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { prepareFormElementProps, processSettingsUrl } from 'components/common/form/formTools';
import { FormComponentBuilder } from 'components/common/form/layout';
import { Params as UrlParams } from 'react-router';

type dataKeyMapProp = {
    id?: string;
    label?: string;
    default?: string;
    readonly?: string;
};

type Required<T> = {
    [P in keyof T]-?: T[P];
};

type dataKeyType = keyof dataKeyMapProp;
type dataKeyFilled = Required<dataKeyMapProp>;

//ToDo divide into smaller parts
export interface FormControlImageListHandlerProps extends FormControlProps {
    dataUrl: string;
    previewUrl: string;
    deleteUrl: string;
    uploadUrl: string;
    uploadFieldKey: string;
    urlParams: any;
    translationsKey: string;
    linkFileIdParamKey: string;
    dataKeyMap: dataKeyMapProp;
}

export default function ImageListHandler({
    controlProps: props,
}: FormElementControlPropsType<FormControlImageListHandlerProps>) {
    const {
        dataUrl,
        previewUrl,
        deleteUrl,
        uploadUrl,
        uploadFieldKey,
        translationsKey,
        linkFileIdParamKey,
        dataKeyMap,
    } = props;

    const { t } = useBundleTranslation(['components/common/image_list_handler']);

    const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
    const [imagesListData, setImagesListData] = useState<GridData>([]);
    const [requestTime, setRequestTime] = useState<number>(Date.now());

    const [previewLink, setPreviewLink] = useState<string>();
    const [nextPreviewLink, setNextPreviewLink] = useState<string>();
    const [previewContainerHeight, setPreviewContainerHeight] = useState<string>('50px');
    const [previewIsLoading, setPreviewIsLoading] = useState<boolean>(true);

    const [showAddPopup, setShowAddPopup] = useState<boolean>(false);
    const [uploadStatus, setUploadStatus] = useState<string>('info');
    const [uploadMessage, setUploadMessage] = useState<string>(t(`${translationsKey}.add_popup.note`));
    const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);

    const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
    const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
    const [deleteStatus, setDeleteStatus] = useState<string>();
    const [deleteMessage, setDeleteMessage] = useState<string>();

    const UPLOAD_FIELD_NAME = props.name + '__upload';
    const LINKS = {
        DATA: processSettingsUrl(dataUrl, Object.keys(props.urlParams), props.urlParams),
        PREVIEW: processSettingsUrl(previewUrl, Object.keys(props.urlParams), props.urlParams),
        DELETE: processSettingsUrl(deleteUrl, Object.keys(props.urlParams), props.urlParams),
        UPLOAD: processSettingsUrl(uploadUrl, Object.keys(props.urlParams), props.urlParams),
    };

    //Create map for assign data keys from backend to common
    const buildKeysMap = () => {
        const dataKeyList: dataKeyType[] = ['id', 'label', 'default', 'readonly'];
        let tmpDataKey = {} as dataKeyMapProp;
        dataKeyList.forEach((key) => {
            tmpDataKey[key] = dataKeyMap[key] ?? key;
        });
        return tmpDataKey as dataKeyFilled;
    };
    const dataKey = buildKeysMap();

    const updateSelectedItemIndex = (newValue: number) => {
        const newIndex = newValue > 0 ? newValue : 0;
        setSelectedItemIndex(newIndex);
    };

    const putFileIdToUrl = (link: string) => {
        const fileId = imagesListData[selectedItemIndex][dataKey.id];
        return link.replace(linkFileIdParamKey, fileId as string);
    };

    //Get data from backend. requestTime - param for refetch data
    const key = [props.name, props.form.formDidMount, requestTime];
    const { data, isLoading } = useQuery<any, Error>({
        queryKey: key,
        queryFn: () => {
            if (props.form.formDidMount) {
                return customRequestAPI.get(LINKS.DATA);
            }
            return false;
        },
    });

    //Update data from request and set selected index
    useEffect(() => {
        const listData: GridData = data?.data ?? [];
        setImagesListData(listData);

        const selectedIndex = listData.findIndex((item) => {
            return item[dataKey.default] === 'Y';
        });
        updateSelectedItemIndex(selectedIndex);
    }, [data]);

    //Update preview link
    useEffect(() => {
        if (imagesListData.length) {
            const link = putFileIdToUrl(LINKS.PREVIEW);
            setNextPreviewLink(link);
        }
    }, [selectedItemIndex, imagesListData]);

    //Loading status turn on for preview image when get new preview link
    useEffect(() => {
        setPreviewIsLoading(previewLink !== nextPreviewLink);
    }, [nextPreviewLink]);

    //Loading status turnoff when preview image is loaded
    useEffect(() => {
        setPreviewIsLoading(false);
    }, [previewLink]);

    //Describe columns for table
    const columns = [
        {
            name: 'display_text',
            title: t(`${translationsKey}.column_title`),
            resizable: false,
        },
    ];

    //Prepare data for table
    const formattedImagesListData = imagesListData.map((item, index) => {
        const isSelected = index === selectedItemIndex;
        return {
            ...item,
            display_text: {
                component: 'selected',
                config: {
                    text: item[dataKey.label],
                    subText: isSelected ? t(`${translationsKey}.selected_row_text`) : undefined,
                    icon: isSelected
                        ? {
                              type: 'mi',
                              value: 'chevron-right',
                          }
                        : undefined,
                } as TransformedComponentConfig,
            },
        };
    });

    //Remove Item action
    const removeItem = () => {
        const deleteLink = putFileIdToUrl(LINKS.DELETE);
        setDeleteInProgress(true);
        customRequestAPI
            .delete(deleteLink)
            .then((response) => {
                if (response.data?.status === 'OK') {
                    setShowDeletePopup(false);
                    //setDeleteStatus('success');
                    //setDeleteMessage(t(response.data.message));
                    setRequestTime(Date.now());
                    updateSelectedItemIndex(0);
                }
                if (response?.data?.status === 'ERROR') {
                    setDeleteStatus('error');
                    setDeleteMessage(t(response.data.message));
                }
                setDeleteInProgress(false);
            })
            .catch(() => {});
    };

    //Configuration upload Field
    const { setValue: hookFormSetValue, control, getValues: hookFromGetValues, watch } = useForm({});
    const formActions = {
        hookFormControl: control,
        hookFormGetValues: hookFromGetValues,
        hookFormSetValue: hookFormSetValue,
        hookFormWatch: watch,
        formDidMount: true,
    } as FormRendererAPIType;

    const uploadImageControlProps = {
        form: formActions,
        component: {
            component: 'UploadImage',
            name: UPLOAD_FIELD_NAME,
            label: t(`${translationsKey}.add_popup.field_label`),
            props: {
                view: 'field',
                hidePreview: true,
                buttonText: t(`${translationsKey}.add_popup.field_btn`),
                inputPlaceholder: t(`${translationsKey}.add_popup.field_placeholder`),
                sx: {
                    mb: 2,
                    '&, .upload-file, .upload-file > div, .upload-file > div > label, .upload-file > div > label div': {
                        width: 1,
                    },
                },
            },
        } as RawFormComponentType,
    };

    const resetUploadState = () => {
        setUploadMessage(t(`${translationsKey}.add_popup.note`));
        setUploadStatus('info');
    };

    //Watch Upload Image Field
    const uploadImageValue = formActions.hookFormWatch(UPLOAD_FIELD_NAME);
    useEffect(() => {
        const files = hookFromGetValues(UPLOAD_FIELD_NAME);

        if (files && files.length) {
            setUploadInProgress(true);
            const formData = new FormData();
            formData.append(uploadFieldKey, files[0]);

            customRequestAPI
                .post(LINKS.UPLOAD, formData)
                .then((response: any) => {
                    if (response?.data?.status === 'ERROR') {
                        setUploadStatus('error');
                        setUploadMessage(
                            t(`${translationsKey}.add_popup.upload_status.error_prefix`) +
                                ' ' +
                                t(response.data.message),
                        );
                    }
                    if (response?.data?.status === 'OK') {
                        setUploadStatus('success');
                        setUploadMessage(t(`${translationsKey}.add_popup.upload_status.success`));
                        setRequestTime(Date.now());
                    }
                    setUploadInProgress(false);
                })
                .catch(() => {});
        } else {
            resetUploadState();
        }
    }, [uploadImageValue]);

    //Reset status message for delete popup
    useEffect(() => {
        if (!showDeletePopup) {
            setDeleteStatus('');
            setDeleteMessage('');
        }
    }, [showDeletePopup]);

    //Reset timer for add popup if closed early as delay
    useEffect(() => {
        let uploadTimerId: any;
        if (uploadStatus === 'success') {
            uploadTimerId = setTimeout(function () {
                setShowAddPopup(false);
                resetUploadState();
            }, 4000);
        }

        return () => clearTimeout(uploadTimerId);
    }, [uploadStatus]);

    //Custom loading mask
    const loadingMask = <LoadingPlaceholder variant={'secondary'} relativeToParent />;

    const imagePreview = useRef<HTMLImageElement>(null);
    const containerPreview = useRef<HTMLDivElement>(null);

    //Animation on change preview image
    const handleImageLoaded = () => {
        const containerWidth = containerPreview?.current?.offsetWidth ?? 0;
        const imageWidth = imagePreview?.current?.width ?? 0;
        const imageHeight = imagePreview?.current?.height ?? 0;
        let containerHeight;

        if (containerWidth && imageWidth && imageHeight) {
            containerHeight =
                (imageWidth < containerWidth ? imageHeight : (containerWidth / imageWidth) * imageHeight) + 'px';
        } else {
            containerHeight = 'auto';
        }
        setPreviewContainerHeight(containerHeight);
        setPreviewLink(nextPreviewLink);
    };

    return (
        <Box>
            {formattedImagesListData && (
                <Box sx={styles.wrapper}>
                    <Box ref={containerPreview} sx={{ ...styles.previewContainer, height: previewContainerHeight }}>
                        {previewIsLoading && loadingMask}
                        <img src={previewLink} style={!previewIsLoading ? { display: 'none' } : {}} alt="" />
                        <img
                            src={nextPreviewLink}
                            style={previewIsLoading ? { display: 'none' } : {}}
                            alt=""
                            ref={imagePreview}
                            onLoad={handleImageLoaded}
                        />
                    </Box>
                    <Box sx={{ mb: 1 }}>
                        <FormStaticLabel
                            elementProps={prepareFormElementProps({
                                form: formActions,
                                component: {
                                    label: t(`${translationsKey}.grid_heading`),
                                    props: {
                                        isSectionLabel: true,
                                    },
                                } as RawFormComponentType,
                            })}
                        />
                    </Box>

                    <MITable
                        autoHeight
                        gridName={'image_list'}
                        data={formattedImagesListData}
                        isLoading={isLoading}
                        height={200}
                        columns={columns}
                        massActionsConfig={{ enabled: false }}
                        handleClickRow={(rowData) => {
                            const index = imagesListData.findIndex((item) => {
                                return item[dataKey.id] === rowData[dataKey.id];
                            });
                            updateSelectedItemIndex(index);
                        }}
                    />
                    <Box>
                        <Button
                            variant="light"
                            color="success"
                            startIcon={<IconMi icon="new" />}
                            onClick={() => {
                                setShowAddPopup(true);
                            }}
                            sx={{ mr: 1 }}
                        >
                            {t(`${translationsKey}.add_button`)}
                        </Button>
                        <Button
                            disabled={imagesListData[selectedItemIndex]?.[dataKey.readonly] === 'Y'}
                            variant="outlined"
                            startIcon={<IconMi icon="delete" />}
                            onClick={() => {
                                setShowDeletePopup(true);
                            }}
                        >
                            {t(`${translationsKey}.delete_button`)}
                        </Button>
                    </Box>
                    <Popup
                        settings={{
                            title: t(`${translationsKey}.delete_popup.title`),
                            textOK: t(`${translationsKey}.delete_popup.delete_button`),
                        }}
                        open={showDeletePopup}
                        onHide={() => {
                            setShowDeletePopup(false);
                        }}
                        onConfirm={removeItem}
                        maxWidth="popupMd"
                    >
                        {deleteInProgress && loadingMask}
                        <Typography>{t(`${translationsKey}.delete_popup.text`)}</Typography>
                        {deleteStatus && (
                            <Box>
                                <FormStaticInfo
                                    elementProps={prepareFormElementProps({
                                        form: formActions,
                                        component: {
                                            label: deleteMessage,
                                            props: {
                                                type: deleteStatus,
                                            },
                                        } as RawFormComponentType,
                                    })}
                                />
                            </Box>
                        )}
                    </Popup>

                    <Popup
                        settings={{
                            title: t(`${translationsKey}.add_popup.title`),
                            noButtons: true,
                        }}
                        open={showAddPopup}
                        onHide={() => {
                            setShowAddPopup(false);
                            resetUploadState();
                        }}
                        maxWidth="popupMd"
                    >
                        {uploadInProgress && loadingMask}
                        <Box sx={{ mb: 0.5 }}>{t(`${translationsKey}.add_popup.field_label`)}</Box>
                        <ReactHookFormController elementProps={prepareFormElementProps(uploadImageControlProps)} />
                        <Box>
                            <FormStaticInfo
                                elementProps={prepareFormElementProps({
                                    form: formActions,
                                    component: {
                                        label: uploadMessage,
                                        props: {
                                            type: uploadStatus,
                                        },
                                    } as RawFormComponentType,
                                })}
                            />
                        </Box>
                    </Popup>
                </Box>
            )}
        </Box>
    );
}

export class ImageListHandlerBuilder extends FormComponentBuilder {
    prepareProps(): FormControlImageListHandlerProps {
        return {
            ...this.controlProps,
            dataUrl: this.elementProps.componentProps.dataUrl,
            previewUrl: this.elementProps.componentProps.previewUrl,
            deleteUrl: this.elementProps.componentProps.deleteUrl,
            uploadUrl: this.elementProps.componentProps.uploadUrl,
            uploadFieldKey: this.elementProps.componentProps.uploadFieldKey,
            translationsKey: this.elementProps.componentProps.translationsKey,
            linkFileIdParamKey: this.elementProps.componentProps.linkFileIdParamKey ?? 'fileId',
            dataKeyMap: this.elementProps.componentProps.dataKeyMap ?? {},
            urlParams: {
                ...this.elementProps.urlParams,
                ...this.elementProps.componentProps?.urlParams,
            } as UrlParams,
        };
    }
}
